import AppContext from "@/AppContext";
import Spinner from "@/components/Spinner";
import Layout from "@/components/elements/Layout";
import ArticleCard from "@/components/items/ArticleCard";
import LoadingContent from "@/components/loader/LoadingContent";
import Schema from "@/components/schema/Schema";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useGetContentList } from "@/hooks/content";
import { getContentsByContentTypeName } from "@/services/api";
import { CONTENT_TYPES } from "@/utils/content";
import { MagnifyingGlass, ArrowsDownUp } from "@phosphor-icons/react";
import { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CaretDown, X } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { useRouter } from "next/router";
import { getSEOByPathname, getSEOByTitle } from "@/services/api/SEO";
import SortMobile from "@/components/drawer/sort-mobile";

export default function Articles({ articles, seoData }) {
  const appContext = useContext(AppContext);
  const isMobile = useIsMobile();
  const router = useRouter();
  const onFetchError = function (error) {
    console.log("Error", error);
    toast.error("An error occured when fetching report data!");
  };
  const {
    contents,
    isLoading,
    currentPage,
    fetchContents,
    search,
    setSearch,
    topic,
    setTopic,
    total,
    lastPage,
    sortInput,
    setSortInput,
    tags,
    setTags,
  } = useGetContentList({
    type: CONTENT_TYPES.ARTICLES,
    onErrorCallback: onFetchError,
    // initialData: articles,
    is_random: true,
  });

  const handleScroll = () => {
    const isBottom =
      window.innerHeight + window.scrollY >= document.body.offsetHeight - 1;
    if (isBottom && currentPage < lastPage && !isLoading) {
      fetchContents(currentPage + 1);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [currentPage, lastPage, isLoading]);

  useEffect(() => {
    const { q } = router.query;
    if (q) {
      setSearch(q);
    }
  }, [router.query]);

  const structuredData = [
    {
      "@context": "https://schema.org/",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: `${process.env.BASE_URL}`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Article",
          item: `${process.env.BASE_URL}articles`,
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: articles.map((item, index) => {
        return {
          "@type": "ListItem",
          position: index + 1,
          url: `${process.env.BASE_URL}articles/${item.slug}`,
          name: item.title,
        };
      }),
    },
  ];

  const onChange = (e) => {
    setSortInput(e);
  };

  const [selectedTopics, setSelectedTopics] = useState([]);
  const [openTopicFilter, setOpenTopicFilter] = useState(false);
  const [searchTopicQuery, setSearchTopicQuery] = useState("");
  const [tempSelectedTopics, setTempSelectedTopics] = useState([]);

  const filteredTopics = appContext.state.topic_list.filter((topic) =>
    topic.name.toLowerCase().includes(searchTopicQuery.toLowerCase())
  );

  const handleTopicChange = (event, topic) => {
    const isChecked = event.target.checked;
    setTempSelectedTopics((prev) =>
      isChecked ? [...prev, topic] : prev.filter((t) => t.uuid !== topic.uuid)
    );
  };

  const applyTopicFilter = () => {
    setSelectedTopics(tempSelectedTopics);
    setTags(tempSelectedTopics.map((topic) => topic.name).join(";"));
    setOpenTopicFilter(false);
  };

  const removeTopicFilter = () => {
    setTempSelectedTopics([]);
    setSelectedTopics([]);
    setTags("");
  };

  useEffect(() => {
    setTempSelectedTopics(selectedTopics);
  }, [openTopicFilter]);

  const handleSearchInput = (evt) => {
    const searchValue = evt.target.value;
    if (searchValue === "" && sortInput === "") {
      setSortInput("newest");
    }
    setSearch(searchValue);

    // Remove 'q' parameter from URL when search is cleared, only if it exists
    if (searchValue === "" && router.query.q) {
      const { q, ...query } = router.query;
      router.push(
        {
          pathname: router.pathname,
          query: query,
        },
        undefined,
        { shallow: true }
      );
    }
  };

  return (
    <div>
      <Schema data={structuredData} />
      <Layout
        seoData={seoData}
        title='Articles'
        description={
          "Explore the latest insights on venture capital and startup funding rounds across the MENAPT region."
        }
      >
        <div className='container py-8'>
          <h1 className='text-4xl font-semibold leading-tight text-white lg:font-bold lg:text-6xl font-display'>
            Articles
          </h1>
          <p className='mt-3 text-base leading-relaxed lg:mt-5 lg:text-lg text-white/50'>
            {seoData.page_description ||
              `Our articles unpack how founders, investors, and policymakers are building the technology economy across the Middle East, North Africa, Pakistan, and Turkiye. Each piece is reported by our editorial team and regional contributors, covering funding rounds, sector deep dives, founder playbooks, and the regulation shaping them. Browse the latest below, or filter by topic to follow a single sector — fintech, proptech, AI, e-commerce, or climate tech — as it develops.`}
          </p>
          <div className='flex flex-wrap items-center mt-5 gap-x-2 border-white/10 gap-y-3 lg:mt-8'>
            <div className='relative w-full'>
              <div className='absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none opacity-40'>
                <MagnifyingGlass className='text-lg' />
              </div>
              <Input
                type='text'
                placeholder='Search...'
                value={search}
                onInput={handleSearchInput}
                className='w-full transition-colors text-sm lg:text-base placeholder:text-white/40 !h-10 lg:!h-12 text-white rounded-lg bg-white/10 pl-10 focus:bg-white/15'
              />
            </div>
            <p className='flex-1 text-xs lg:text-base'>
              <span className='opacity-30'>Showing</span>{" "}
              {contents.length > 0 ? `1 - ${contents.length}` : 0}{" "}
              <span className='opacity-30'>of</span> {total}
            </p>
            {!isMobile ? (
              <Select value={sortInput} onValueChange={onChange}>
                <SelectTrigger
                  hideDropdownIcon
                  className='w-auto text-white border h-9 border-white/5 bg-white/5 hover:bg-white/10'
                >
                  <div className='flex items-center space-x-2'>
                    <ArrowsDownUp className='text-base lg:text-lg' />
                    <SelectValue placeholder={"Most Recent"} />
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={"newest"}>Most Recent</SelectItem>
                  <SelectItem value={"most_viewed"}>Most Viewed</SelectItem>
                  {search && <SelectItem value={""}>Most Relevant</SelectItem>}
                </SelectContent>
              </Select>
            ) : (
              <SortMobile
                sortOption={sortInput}
                onChange={onChange}
                options={[
                  { value: "newest", label: "Most Recent" },
                  { value: "most_viewed", label: "Most Viewed" },
                  { value: "", label: "Most Relevant" },
                ]}
              />
            )}
            <Popover open={openTopicFilter} onOpenChange={setOpenTopicFilter}>
              <PopoverTrigger asChild>
                <Button
                  onClick={() => setOpenTopicFilter(true)}
                  className='text-white border h-9 border-white/5 bg-white/5 hover:bg-white/10'
                >
                  Topics
                  {selectedTopics.length > 0 && (
                    <span className='ml-1 inline-flex items-center justify-center w-4 h-4 font-semibold bg-white text-[10px] tracking-tighter leading-none rounded-full text-background'>
                      <span>{selectedTopics.length}</span>
                    </span>
                  )}
                  <CaretDown className='size-4 ml-2.5 -mr-1' />
                </Button>
              </PopoverTrigger>
              <PopoverContent className='w-64 bg-black h-80'>
                <div>
                  <Input
                    type='text'
                    placeholder='Search topics...'
                    value={searchTopicQuery}
                    onChange={(e) => setSearchTopicQuery(e.target.value)}
                    className='mb-2'
                  />
                </div>
                <ScrollArea className='w-56 h-56'>
                  {filteredTopics.map((topic, index) => (
                    <div key={index} className='flex items-center p-2 text-xs'>
                      <input
                        type='checkbox'
                        checked={
                          !!tempSelectedTopics.find(
                            (t) => t.uuid === topic.uuid
                          )
                        }
                        onChange={(event) => handleTopicChange(event, topic)}
                        id={`topic-${topic.uuid}`}
                        className='mr-2'
                      />
                      <label htmlFor={`topic-${topic.uuid}`}>
                        {topic.name}
                      </label>
                    </div>
                  ))}
                </ScrollArea>
                <div className='border-b border-r border-l rounded-b mt-2 pt-2 flex items-center justify-between p-2 bg-black -ml-4 w-[114.8%]'>
                  <Button
                    onClick={removeTopicFilter}
                    size='sm'
                    variant='link'
                    className='text-xs text-red-500 h-7'
                    disabled={selectedTopics.length <= 0}
                  >
                    Reset
                  </Button>
                  <Button
                    onClick={applyTopicFilter}
                    size='sm'
                    variant='outline'
                    className='text-xs h-7'
                  >
                    Apply Filter
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
            {selectedTopics && selectedTopics.length > 0 && (
              <div className='flex flex-wrap order-5 w-full gap-2'>
                {selectedTopics.map((topic) => (
                  <Badge
                    key={`selected-topic-${topic.uuid}`}
                    variant='secondary'
                    className='text-white bg-white/10 hover:bg-white/20'
                  >
                    {topic.name}
                    <button
                      className='ml-1 hover:text-red-500'
                      onClick={() => {
                        setSelectedTopics((prev) =>
                          prev.filter((t) => t.uuid !== topic.uuid)
                        );
                        setTags((prev) =>
                          prev
                            .split(";")
                            .filter((t) => t !== topic.name)
                            .join(";")
                        );
                      }}
                    >
                      <X size={14} />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
          {contents.length > 0 && (
            <h2 className='pt-8 text-xl font-semibold tracking-tight lg:pt-10 lg:text-2xl'>
              Latest MENAPT Startup Articles
            </h2>
          )}
          {contents.length > 0 ? (
            <div className='grid grid-cols-2 gap-4 pt-5 lg:pt-8 lg:grid-cols-4 xl:grid-cols-6'>
              {contents.map((item) => {
                return <ArticleCard data={item} key={`article-${item.uuid}`} />;
              })}
            </div>
          ) : !isLoading ? (
            <div className='flex flex-col items-center justify-center py-16'>
              <MagnifyingGlass
                size={48}
                className='mb-4 text-white opacity-50'
              />
              <p className='text-lg text-white text-opacity-80'>
                No articles found
              </p>
              <p className='mt-2 text-white text-opacity-60'>
                Try adjusting your search or filters
              </p>
            </div>
          ) : null}
          {isLoading && (
            <div className='flex justify-center mt-8'>
              <LoadingContent />
            </div>
          )}
        </div>
      </Layout>
    </div>
  );
}

export async function getServerSideProps() {
  let articles = [];
  try {
    const articlesData = await getContentsByContentTypeName(
      CONTENT_TYPES.ARTICLES
    );
    articles = articlesData?.data?.data || [];
  } catch (error) {
    console.error("Error fetching articles for structured data:", error);
  }

  let seoData = {};
  try {
    const seoResponse = await getSEOByPathname("articles");
    seoData = seoResponse.data;
  } catch (error) {
    console.error("Error fetching SEO data:", error);
  }

  return {
    props: {
      articles,
      seoData,
    },
  };
}
